/**
 * Intent Classifier — Local keyword/regex scoring to detect which MCP services
 * a user message needs. Runs entirely in the browser with ZERO token cost.
 *
 * Activation: Only runs when total enabled tools > CLASSIFIER_THRESHOLD (60).
 * Below that, all enabled services are sent without filtering.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ServiceScore {
  service: string
  score: number
  matchedKeywords: string[]
  matchedPatterns: string[]
}
interface KeywordEntry {
  word: string
  weight: number
}
interface PatternEntry {
  regex: RegExp
  weight: number
  description: string
}
interface ServiceConfig {
  keywords: KeywordEntry[]
  patterns: PatternEntry[]
  baseWeight: number
  alwaysInclude: boolean
}
interface ClassifierResult {
  services: string[]
  scores: ServiceScore[]
  classifierActive: boolean
  totalToolCount: number
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSIFIER_THRESHOLD = 60
const SCORE_THRESHOLD = 15
const MAX_SERVICES = 5
const FALLBACK_SERVICES = ['jira', 'confluence']

// ─── Service Registry ─────────────────────────────────────────────────────────

const SERVICE_REGISTRY: Record<string, ServiceConfig> = {
  jira: {
    baseWeight: 5,
    alwaysInclude: true,
    keywords: [
      // Entities — high weight
      {
        word: 'issue',
        weight: 30,
      },
      {
        word: 'issues',
        weight: 30,
      },
      {
        word: 'story',
        weight: 30,
      },
      {
        word: 'stories',
        weight: 30,
      },
      {
        word: 'bug',
        weight: 30,
      },
      {
        word: 'bugs',
        weight: 30,
      },
      {
        word: 'epic',
        weight: 30,
      },
      {
        word: 'epica',
        weight: 30,
      },
      {
        word: 'épica',
        weight: 30,
      },
      {
        word: 'épicas',
        weight: 30,
      },
      {
        word: 'epicas',
        weight: 30,
      },
      {
        word: 'task',
        weight: 25,
      },
      {
        word: 'tarea',
        weight: 25,
      },
      {
        word: 'tareas',
        weight: 25,
      },
      {
        word: 'subtask',
        weight: 30,
      },
      {
        word: 'subtarea',
        weight: 30,
      },
      {
        word: 'sprint',
        weight: 30,
      },
      {
        word: 'sprints',
        weight: 30,
      },
      {
        word: 'backlog',
        weight: 30,
      },
      {
        word: 'board',
        weight: 25,
      },
      {
        word: 'kanban',
        weight: 25,
      },
      {
        word: 'jira',
        weight: 40,
      },
      // Actions — medium weight
      {
        word: 'asignar',
        weight: 20,
      },
      {
        word: 'assign',
        weight: 20,
      },
      {
        word: 'transicionar',
        weight: 20,
      },
      {
        word: 'prioridad',
        weight: 20,
      },
      {
        word: 'priority',
        weight: 20,
      },
      {
        word: 'workflow',
        weight: 25,
      },
      {
        word: 'comentario',
        weight: 15,
      },
      {
        word: 'comment',
        weight: 15,
      },
      {
        word: 'worklog',
        weight: 25,
      },
      {
        word: 'estimación',
        weight: 20,
      },
      {
        word: 'puntos',
        weight: 15,
      },
      {
        word: 'points',
        weight: 15,
      },
      {
        word: 'velocity',
        weight: 25,
      },
      // Agile context — low weight
      {
        word: 'scrum',
        weight: 15,
      },
      {
        word: 'daily',
        weight: 10,
      },
      {
        word: 'standup',
        weight: 15,
      },
      {
        word: 'retro',
        weight: 15,
      },
      {
        word: 'planning',
        weight: 15,
      },
      {
        word: 'bloqueador',
        weight: 20,
      },
      {
        word: 'blocked',
        weight: 20,
      },
      {
        word: 'impedimento',
        weight: 20,
      },
      {
        word: 'proyecto',
        weight: 10,
      },
      {
        word: 'project',
        weight: 10,
      },
    ],
    patterns: [
      {
        regex: /[A-Z]{2,10}-\d+/,
        weight: 50,
        description: 'Jira issue key (PROJ-123)',
      },
      {
        regex: /project\s*=\s*\w+/i,
        weight: 40,
        description: 'JQL project filter',
      },
      {
        regex: /sprint\s+(actual|activo|active)/i,
        weight: 35,
        description: 'Active sprint query',
      },
      {
        regex: /estado del sprint/i,
        weight: 35,
        description: 'Sprint status query',
      },
      {
        regex: /cuánt[oa]s?\s+(issue|bug|stor|epic|tarea)/i,
        weight: 30,
        description: 'Count query',
      },
      {
        regex: /listame\s+(las?|los?)\s+(issue|bug|stor|epic|tarea)/i,
        weight: 30,
        description: 'List query',
      },
      {
        regex: /jql/i,
        weight: 40,
        description: 'JQL mention',
      },
    ],
  },
  confluence: {
    baseWeight: 3,
    alwaysInclude: false,
    keywords: [
      {
        word: 'confluence',
        weight: 40,
      },
      {
        word: 'wiki',
        weight: 30,
      },
      {
        word: 'página',
        weight: 25,
      },
      {
        word: 'pagina',
        weight: 25,
      },
      {
        word: 'page',
        weight: 20,
      },
      {
        word: 'páginas',
        weight: 25,
      },
      {
        word: 'pages',
        weight: 20,
      },
      {
        word: 'espacio',
        weight: 25,
      },
      {
        word: 'space',
        weight: 15,
      },
      {
        word: 'espacios',
        weight: 25,
      },
      {
        word: 'spaces',
        weight: 15,
      },
      {
        word: 'documentación',
        weight: 25,
      },
      {
        word: 'documentation',
        weight: 25,
      },
      {
        word: 'adjunto',
        weight: 20,
      },
      {
        word: 'attachment',
        weight: 20,
      },
    ],
    patterns: [
      {
        regex: /busca[r]?\s+(en\s+)?confluence/i,
        weight: 40,
        description: 'Search in Confluence',
      },
      {
        regex: /crea[r]?\s+(una?\s+)?págin/i,
        weight: 35,
        description: 'Create page',
      },
      {
        regex: /documentación\s+de/i,
        weight: 25,
        description: 'Documentation query',
      },
      {
        regex: /exporta[r]?\s+(a\s+)?confluence/i,
        weight: 40,
        description: 'Export to Confluence',
      },
    ],
  },
  gmail: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'gmail',
        weight: 50,
      },
      {
        word: 'email',
        weight: 40,
      },
      {
        word: 'emails',
        weight: 40,
      },
      {
        word: 'mail',
        weight: 35,
      },
      {
        word: 'mails',
        weight: 35,
      },
      {
        word: 'correo',
        weight: 40,
      },
      {
        word: 'correos',
        weight: 40,
      },
      {
        word: 'inbox',
        weight: 35,
      },
      {
        word: 'bandeja',
        weight: 35,
      },
      {
        word: 'borrador',
        weight: 30,
      },
      {
        word: 'draft',
        weight: 30,
      },
      {
        word: 'reenviar',
        weight: 30,
      },
      {
        word: 'forward',
        weight: 30,
      },
      {
        word: 'no leído',
        weight: 30,
      },
      {
        word: 'unread',
        weight: 30,
      },
      {
        word: 'destacado',
        weight: 25,
      },
      {
        word: 'starred',
        weight: 25,
      },
    ],
    patterns: [
      {
        regex: /envia[r]?\s+(un\s+)?(mail|email|correo)/i,
        weight: 45,
        description: 'Send email',
      },
      {
        regex: /lee[r]?\s+(mis?\s+)?(mail|email|correo)/i,
        weight: 40,
        description: 'Read email',
      },
      {
        regex: /últimos?\s+(mail|email|correo)/i,
        weight: 35,
        description: 'Recent emails',
      },
      {
        regex: /mail\s+(de|from|para|to)\s+\w/i,
        weight: 35,
        description: 'Email from/to',
      },
      {
        regex: /[\w.-]+@[\w.-]+\.\w+/,
        weight: 20,
        description: 'Email address detected',
      },
    ],
  },
  calendar: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'calendar',
        weight: 40,
      },
      {
        word: 'calendario',
        weight: 40,
      },
      {
        word: 'evento',
        weight: 35,
      },
      {
        word: 'eventos',
        weight: 35,
      },
      {
        word: 'event',
        weight: 35,
      },
      {
        word: 'reunión',
        weight: 35,
      },
      {
        word: 'reunion',
        weight: 35,
      },
      {
        word: 'meeting',
        weight: 35,
      },
      {
        word: 'agenda',
        weight: 30,
      },
      {
        word: 'cita',
        weight: 30,
      },
      {
        word: 'schedule',
        weight: 25,
      },
      {
        word: 'agendar',
        weight: 30,
      },
      {
        word: 'invitación',
        weight: 25,
      },
    ],
    patterns: [
      {
        regex: /crea[r]?\s+(un\s+)?evento/i,
        weight: 45,
        description: 'Create event',
      },
      {
        regex: /qué\s+tengo\s+(hoy|mañana|esta semana)/i,
        weight: 40,
        description: 'Schedule query',
      },
      {
        regex: /reunión\s+(con|de|para)/i,
        weight: 35,
        description: 'Meeting with',
      },
      {
        regex: /agenda\s+(de|del|para)/i,
        weight: 35,
        description: 'Agenda query',
      },
      {
        regex: /\d{1,2}[:/]\d{2}\s*(am|pm|hs)?/i,
        weight: 15,
        description: 'Time detected',
      },
    ],
  },
  drive: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'drive',
        weight: 40,
      },
      {
        word: 'archivo',
        weight: 20,
      },
      {
        word: 'archivos',
        weight: 20,
      },
      {
        word: 'file',
        weight: 20,
      },
      {
        word: 'carpeta',
        weight: 30,
      },
      {
        word: 'folder',
        weight: 30,
      },
      {
        word: 'compartir',
        weight: 15,
      },
      {
        word: 'share',
        weight: 15,
      },
      {
        word: 'subir',
        weight: 20,
      },
      {
        word: 'upload',
        weight: 20,
      },
      {
        word: 'descargar',
        weight: 20,
      },
      {
        word: 'download',
        weight: 20,
      },
      {
        word: 'permiso',
        weight: 20,
      },
      {
        word: 'permisos',
        weight: 20,
      },
      {
        word: 'almacenamiento',
        weight: 25,
      },
      {
        word: 'storage',
        weight: 25,
      },
    ],
    patterns: [
      {
        regex: /busca[r]?\s+(en\s+)?drive/i,
        weight: 45,
        description: 'Search Drive',
      },
      {
        regex: /archivos?\s+(de|en|compartido)/i,
        weight: 25,
        description: 'Files query',
      },
      {
        regex: /subir\s+(un\s+)?archivo/i,
        weight: 35,
        description: 'Upload file',
      },
    ],
  },
  docs: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'doc',
        weight: 25,
      },
      {
        word: 'docs',
        weight: 30,
      },
      {
        word: 'documento',
        weight: 25,
      },
      {
        word: 'documentos',
        weight: 25,
      },
      {
        word: 'google doc',
        weight: 45,
      },
      {
        word: 'google docs',
        weight: 45,
      },
      {
        word: 'informe',
        weight: 20,
      },
      {
        word: 'reporte',
        weight: 20,
      },
      {
        word: 'redactar',
        weight: 20,
      },
    ],
    patterns: [
      {
        regex: /crea[r]?\s+(un\s+)?(doc|documento|google doc)/i,
        weight: 45,
        description: 'Create doc',
      },
      {
        regex: /exporta[r]?\s+(a\s+)?(doc|google doc)/i,
        weight: 45,
        description: 'Export to doc',
      },
      {
        regex: /arma[r]?\s+(un\s+)?(doc|informe|reporte)/i,
        weight: 40,
        description: 'Build doc',
      },
    ],
  },
  sheets: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'sheet',
        weight: 35,
      },
      {
        word: 'sheets',
        weight: 35,
      },
      {
        word: 'spreadsheet',
        weight: 40,
      },
      {
        word: 'planilla',
        weight: 35,
      },
      {
        word: 'planillas',
        weight: 35,
      },
      {
        word: 'excel',
        weight: 30,
      },
      {
        word: 'celda',
        weight: 25,
      },
      {
        word: 'celdas',
        weight: 25,
      },
      {
        word: 'fila',
        weight: 20,
      },
      {
        word: 'filas',
        weight: 20,
      },
      {
        word: 'columna',
        weight: 20,
      },
    ],
    patterns: [
      {
        regex: /crea[r]?\s+(un\s+)?(sheet|spreadsheet|planilla)/i,
        weight: 45,
        description: 'Create sheet',
      },
      {
        regex: /exporta[r]?\s+(a\s+)?(sheet|excel|planilla)/i,
        weight: 45,
        description: 'Export to sheet',
      },
      {
        regex: /tabla\s+de\s+datos/i,
        weight: 25,
        description: 'Data table',
      },
    ],
  },
  forms: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'form',
        weight: 30,
      },
      {
        word: 'forms',
        weight: 30,
      },
      {
        word: 'formulario',
        weight: 40,
      },
      {
        word: 'formularios',
        weight: 40,
      },
      {
        word: 'encuesta',
        weight: 35,
      },
      {
        word: 'survey',
        weight: 35,
      },
    ],
    patterns: [
      {
        regex: /crea[r]?\s+(un\s+)?formulario/i,
        weight: 45,
        description: 'Create form',
      },
      {
        regex: /respuestas?\s+(del?\s+)?formulario/i,
        weight: 40,
        description: 'Form responses',
      },
      {
        regex: /google\s+form/i,
        weight: 45,
        description: 'Google Form',
      },
    ],
  },
  tasks: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'google tasks',
        weight: 45,
      },
      {
        word: 'to-do',
        weight: 25,
      },
      {
        word: 'todo',
        weight: 15,
      },
      {
        word: 'pendiente',
        weight: 10,
      },
      {
        word: 'pendientes',
        weight: 10,
      },
      {
        word: 'recordatorio',
        weight: 20,
      },
      {
        word: 'reminder',
        weight: 20,
      },
    ],
    patterns: [
      {
        regex: /google\s+tasks?/i,
        weight: 50,
        description: 'Google Tasks explicit',
      },
      {
        regex: /lista\s+de\s+(tareas|to-?do)/i,
        weight: 30,
        description: 'Task list',
      },
      {
        regex: /mis\s+pendientes/i,
        weight: 20,
        description: 'My pending tasks',
      },
    ],
  },
  contacts: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'contacto',
        weight: 40,
      },
      {
        word: 'contactos',
        weight: 40,
      },
      {
        word: 'contact',
        weight: 35,
      },
      {
        word: 'contacts',
        weight: 35,
      },
      {
        word: 'teléfono',
        weight: 25,
      },
      {
        word: 'phone',
        weight: 25,
      },
      {
        word: 'directorio',
        weight: 30,
      },
    ],
    patterns: [
      {
        regex: /busca[r]?\s+(el\s+)?(contacto|teléfono|mail)\s+de/i,
        weight: 40,
        description: 'Find contact',
      },
      {
        regex: /agregar\s+(un\s+)?contacto/i,
        weight: 40,
        description: 'Add contact',
      },
    ],
  },
  chat: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'google chat',
        weight: 45,
      },
      {
        word: 'hangouts',
        weight: 30,
      },
    ],
    patterns: [
      {
        regex: /google\s+chat/i,
        weight: 50,
        description: 'Google Chat explicit',
      },
      {
        regex: /enviar\s+(por|en)\s+chat/i,
        weight: 30,
        description: 'Send via chat',
      },
    ],
  },
  slack: {
    baseWeight: 0,
    alwaysInclude: false,
    keywords: [
      {
        word: 'slack',
        weight: 50,
      },
      {
        word: 'canal',
        weight: 20,
      },
      {
        word: 'channel',
        weight: 20,
      },
    ],
    patterns: [
      {
        regex: /#[\w-]+/,
        weight: 45,
        description: 'Slack channel (#name)',
      },
      {
        regex: /enviar\s+(a|en|por)\s+slack/i,
        weight: 45,
        description: 'Send to Slack',
      },
      {
        regex: /qué\s+se\s+habló\s+en/i,
        weight: 25,
        description: 'What was discussed',
      },
      {
        regex: /mensajes?\s+(de|en)\s+#/i,
        weight: 40,
        description: 'Messages in channel',
      },
    ],
  },
}

// ─── Normalize text (remove accents for matching) ─────────────────────────────

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

// ─── Main Classifier Function ─────────────────────────────────────────────────

export function classifyIntent(
  message: string,
  enabledServices: string[],
  toolCountByService: Record<string, number>,
): ClassifierResult {
  const totalToolCount = Object.values(toolCountByService).reduce(
    (a, b) => a + b,
    0,
  )

  // Below threshold → send all enabled services
  if (totalToolCount <= CLASSIFIER_THRESHOLD) {
    return {
      services: enabledServices,
      scores: enabledServices.map((s) => ({
        service: s,
        score: 100,
        matchedKeywords: ['below-threshold'],
        matchedPatterns: [],
      })),
      classifierActive: false,
      totalToolCount,
    }
  }
  const msgLower = message.toLowerCase()
  const msgNorm = normalize(message)
  const scores: ServiceScore[] = []
  for (const serviceId of enabledServices) {
    const config = SERVICE_REGISTRY[serviceId]
    if (!config) continue

    // Always-include services get max score
    if (config.alwaysInclude) {
      scores.push({
        service: serviceId,
        score: 100,
        matchedKeywords: ['always-include'],
        matchedPatterns: [],
      })
      continue
    }
    let score = config.baseWeight
    const matchedKeywords: string[] = []
    const matchedPatterns: string[] = []

    // Keyword matching with word boundaries
    for (const kw of config.keywords) {
      const kwNorm = normalize(kw.word)
      // Try both normalized and original
      const regexNorm = new RegExp(
        `\\b${kwNorm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
        'i',
      )
      const regexOrig = new RegExp(
        `\\b${kw.word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
        'i',
      )
      if (regexNorm.test(msgNorm) || regexOrig.test(msgLower)) {
        score += kw.weight
        matchedKeywords.push(`${kw.word}(+${kw.weight})`)
      }
    }

    // Pattern matching
    for (const pat of config.patterns) {
      if (pat.regex.test(message) || pat.regex.test(msgLower)) {
        score += pat.weight
        matchedPatterns.push(`${pat.description}(+${pat.weight})`)
      }
    }
    scores.push({
      service: serviceId,
      score,
      matchedKeywords,
      matchedPatterns,
    })
  }

  // Sort by score descending
  scores.sort((a, b) => b.score - a.score)

  // Filter by threshold
  let selected = scores.filter((s) => s.score >= SCORE_THRESHOLD)

  // Fallback if nothing matched
  if (selected.length === 0) {
    selected = scores.filter(
      (s) =>
        FALLBACK_SERVICES.includes(s.service) &&
        enabledServices.includes(s.service),
    )
    // If even fallback services aren't enabled, take the top scorer
    if (selected.length === 0 && scores.length > 0) {
      selected = [scores[0]]
    }
  }

  // Limit to max services
  const result = selected.slice(0, MAX_SERVICES).map((s) => s.service)

  // Log for debugging
  console.log('[IntentClassifier]', {
    message: message.substring(0, 80),
    totalTools: totalToolCount,
    scores: scores.map((s) => `${s.service}:${s.score}`).join(', '),
    selected: result,
  })
  return {
    services: result,
    scores,
    classifierActive: true,
    totalToolCount,
  }
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export { CLASSIFIER_THRESHOLD, SERVICE_REGISTRY }
export type { ClassifierResult, ServiceConfig }
